import reactUpdate from 'immutability-helper';
import {ANVILMembraneOrientation} from 'molstar/lib/commonjs/extensions/anvil/behavior';
import {DnatcoNtCs} from 'molstar/lib/commonjs/extensions/dnatco';
import {
  MAQualityAssessment,
  QualityAssessmentPLDDTPreset,
  QualityAssessmentQmeanPreset,
} from 'molstar/lib/commonjs/extensions/model-archive/quality-assessment/behavior';
import {QualityAssessment} from 'molstar/lib/commonjs/extensions/model-archive/quality-assessment/prop';
import {PDBeStructureQualityReport} from 'molstar/lib/commonjs/extensions/pdbe';
import {RCSBAssemblySymmetry, RCSBValidationReport} from 'molstar/lib/commonjs/extensions/rcsb';
import {VolsegVolumeServerConfig} from 'molstar/lib/commonjs/extensions/volumes-and-segmentations';
import {StructureSelection, Structure} from 'molstar/lib/commonjs/mol-model/structure';
import {
  BoxifyVolumeStreaming,
  CreateVolumeStreamingBehavior,
  InitVolumeStreaming,
} from 'molstar/lib/commonjs/mol-plugin/behavior/dynamic/volume-streaming/transformers';
import {PluginConfig} from 'molstar/lib/commonjs/mol-plugin/config';
import {PluginSpec} from 'molstar/lib/commonjs/mol-plugin/spec';
import {StateActions} from 'molstar/lib/commonjs/mol-plugin-state/actions';
import {DownloadStructure} from 'molstar/lib/commonjs/mol-plugin-state/actions/structure';
import {AssignColorVolume} from 'molstar/lib/commonjs/mol-plugin-state/actions/volume';
import {
  PresetStructureRepresentations,
  StructureRepresentationPresetProvider,
} from 'molstar/lib/commonjs/mol-plugin-state/builder/structure/representation-preset';
import {BuiltInTrajectoryFormat} from 'molstar/lib/commonjs/mol-plugin-state/formats/trajectory';
import {StateTransforms} from 'molstar/lib/commonjs/mol-plugin-state/transforms';
import {PluginUIContext} from 'molstar/lib/commonjs/mol-plugin-ui/context';
import {DefaultPluginUISpec, PluginUISpec} from 'molstar/lib/commonjs/mol-plugin-ui/spec';
import {Script} from 'molstar/lib/commonjs/mol-script/script';
import {StateObjectRef} from 'molstar/lib/commonjs/mol-state';
import {Asset} from 'molstar/lib/commonjs/mol-util/assets';

/*
 * This function basically adapts the spec from the Mol* example app with some minor adjustments:
 * https://github.com/molstar/molstar/blob/1f77b19ced4980500d529cdcea322af30d26a359/src/apps/viewer/app.ts#L121-L175
 *
 * Some default actions and behaviors are omitted to keep the UI simpler, the remoteState component is
 * disabled and the initial layout is tweaked to conserve screen real estate.
 */
export function getPluginState(): PluginUISpec {
  // oxlint-disable-next-line new-cap
  return reactUpdate(DefaultPluginUISpec(), {
    actions: {
      $set: [
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateActions.Structure.EnableModelCustomProps),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateActions.Structure.EnableStructureCustomProps),

        // Volume streaming
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(InitVolumeStreaming),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(BoxifyVolumeStreaming),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(CreateVolumeStreamingBehavior),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseCif),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseCcp4),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Data.ParseDsn6),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromMmCif),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromCifCore),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TrajectoryFromPDB),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.TransformStructureConformation),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureFromModel),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureFromTrajectory),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.ModelFromTrajectory),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Model.StructureSelectionFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsDistance3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsAngle3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsDihedral3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsLabel3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureSelectionsOrientation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ModelUnitcell3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.StructureBoundingBox3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ExplodeStructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.SpinStructureRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.UnwindStructureAssemblyRepresentation3D),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.TransparencyStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ClippingStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.SubstanceStructureRepresentation3DFromScript),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.ThemeStrengthRepresentation3D),

        // oxlint-disable-next-line new-cap
        PluginSpec.Action(AssignColorVolume),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromCcp4),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromDsn6),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromCube),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Volume.VolumeFromDx),
        // oxlint-disable-next-line new-cap
        PluginSpec.Action(StateTransforms.Representation.VolumeRepresentation3D),
      ],
    },
    behaviors: {
      $push: [
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(DnatcoNtCs),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(PDBeStructureQualityReport),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(RCSBAssemblySymmetry),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(RCSBValidationReport),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(ANVILMembraneOrientation),
        // oxlint-disable-next-line new-cap
        PluginSpec.Behavior(MAQualityAssessment),
      ],
    },
    customFormats: {$set: []},
    layout: {
      $set: {
        initial: {
          isExpanded: false,
          showControls: false,
          controlsDisplay: 'reactive',
          regionState: {bottom: 'full', left: 'collapsed', right: 'full', top: 'full'},
        },
      },
    },
    // Set bottom to 'none' to hide logs, but keep all other controls.
    components: {$set: {controls: {bottom: 'none'}, remoteState: 'none'}},
    config: {
      $set: [
        [PluginConfig.General.DisableAntialiasing, PluginConfig.General.DisableAntialiasing.defaultValue],
        [PluginConfig.General.PixelScale, PluginConfig.General.PixelScale.defaultValue],
        [PluginConfig.General.PickScale, PluginConfig.General.PickScale.defaultValue],
        [PluginConfig.General.PickPadding, PluginConfig.General.PickPadding.defaultValue],
        [PluginConfig.General.EnableWboit, PluginConfig.General.EnableWboit.defaultValue],
        [PluginConfig.General.EnableDpoit, PluginConfig.General.EnableDpoit.defaultValue],
        [PluginConfig.General.PreferWebGl1, PluginConfig.General.PreferWebGl1.defaultValue],
        // Try to run even if hardware acceleration is not available.
        [PluginConfig.General.AllowMajorPerformanceCaveat, true],
        [PluginConfig.General.PowerPreference, PluginConfig.General.PowerPreference.defaultValue],
        // Disable full-screen because it takes over the whole screen.
        [PluginConfig.Viewport.ShowExpand, false],
        [PluginConfig.Viewport.ShowControls, PluginConfig.Viewport.ShowControls.defaultValue],
        [PluginConfig.Viewport.ShowSettings, PluginConfig.Viewport.ShowSettings.defaultValue],
        [PluginConfig.Viewport.ShowSelectionMode, PluginConfig.Viewport.ShowSelectionMode.defaultValue],
        [PluginConfig.Viewport.ShowAnimation, PluginConfig.Viewport.ShowAnimation.defaultValue],
        [
          PluginConfig.Viewport.ShowTrajectoryControls,
          PluginConfig.Viewport.ShowTrajectoryControls.defaultValue,
        ],
        // remoteState is set to 'none' above, but set the servers to empty strings to be safe.
        [PluginConfig.State.DefaultServer, ''],
        [PluginConfig.State.CurrentServer, ''],
        [PluginConfig.VolumeStreaming.DefaultServer, ''],
        [PluginConfig.VolumeStreaming.Enabled, false],
        [PluginConfig.Download.DefaultPdbProvider, 'pdbe'],
        [PluginConfig.Download.DefaultEmdbProvider, 'pdbe'],
        [PluginConfig.Structure.DefaultRepresentationPreset, ViewerAutoPreset.id],
        [PluginConfig.Structure.SaccharideCompIdMapType, 'default'],
        [VolsegVolumeServerConfig.DefaultServer, ''],
      ],
    },
  });
}

export async function downloadRemoteStructure(
  pluginContext: PluginUIContext,
  url: string,
  format: BuiltInTrajectoryFormat
): Promise<void> {
  // Clear any existing structures first to prevent accumulation
  await pluginContext.clear();

  const defaultParams = DownloadStructure.createDefaultParams(
    pluginContext.state.data.root.obj!,
    pluginContext
  );
  await pluginContext.runTask(
    pluginContext.state.data.applyAction(DownloadStructure, {
      source: {
        name: 'url',
        params: {
          // oxlint-disable-next-line new-cap
          url: Asset.Url(url),
          format,
          isBinary: false,
          // Note: can pass in a label here now, maybe should generate one like `AlphaFold 2 run on ${date}`.
          label: undefined,
          options: {...defaultParams.source.params.options, representationParams: undefined},
        },
      },
    })
  );
}

// TODO: this was copy-pasted from molstar with some minor changes from linting - it is exported but trying to import currently fails.
// oxlint-disable-next-line new-cap
export const ViewerAutoPreset = StructureRepresentationPresetProvider({
  id: 'preset-structure-representation-viewer-auto',
  display: {
    name: 'Automatic (w/ Annotation)',
    group: 'Annotation',
    description:
      'Show standard automatic representation but colored by quality assessment (if available in the model).',
  },
  isApplicable(a: unknown) {
    return (
      // @ts-expect-error
      // eslint-disable-next-line no-restricted-syntax
      !!a.data.models.some((m) => QualityAssessment.isApplicable(m, 'pLDDT')) || // @ts-expect-error
      // eslint-disable-next-line no-restricted-syntax
      !!a.data.models.some((m) => QualityAssessment.isApplicable(m, 'qmean'))
    );
  },
  params: () => StructureRepresentationPresetProvider.CommonParams,
  async apply(ref, params, plugin) {
    const structureCell = StateObjectRef.resolveAndCheck(plugin.state.data, ref);
    const structure = structureCell?.obj?.data;
    if (!structureCell || !structure) {
      return {};
    }

    if (structure.models.some((m) => QualityAssessment.isApplicable(m, 'pLDDT'))) {
      return await QualityAssessmentPLDDTPreset.apply(ref, params, plugin);
    } else if (structure.models.some((m) => QualityAssessment.isApplicable(m, 'qmean'))) {
      return await QualityAssessmentQmeanPreset.apply(ref, params, plugin);
    } else {
      return await PresetStructureRepresentations.auto.apply(ref, params, plugin);
    }
  },
});

export function selectStructureResidues(
  pluginContext: PluginUIContext,
  structure: Structure,
  residueStart: number,
  residueEnd: number
): void {
  const sel = Script.getStructureSelection(
    (Q) =>
      Q.struct.generator.atomGroups({
        'residue-test': Q.core.rel.inRange([
          Q.struct.atomProperty.macromolecular.auth_seq_id(),
          // AlphaFold structures seem to be 1-indexed and Mol* applies this range inclusively,
          // so adjust the start index but not the end index.
          residueStart + 1,
          residueEnd,
        ]),
      }),
    structure
  );
  const loci = StructureSelection.toLociWithSourceUnits(sel);
  pluginContext.managers.interactivity.lociSelects.selectOnly({loci});
}
